import { ActionIcon, Container, Flex, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconEdit, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react'
import { useDeleteRack, usePlacesConfigs } from 'hooks/places'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo } from 'react'
import { PlaceConfig } from 'types/places'

export default function PlacesTable() {
  const deleteRack = useDeleteRack()
  function openUpdatePlaceConfigModal(place: PlaceConfig) {
    modals.openContextModal({
      modal: 'updatePlaceConfig',
      centered: true,
      title: 'Edytuj regał',
      innerProps: {
        initialValues: place,
      },
    })
  }

  function openDeleteRackModal(place: PlaceConfig) {
    modals.openConfirmModal({
      title: 'Usuń regał',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć regał <b>{place.rack}</b>? Nie będzie można cofnąć tej operacji.
        </Text>
      ),
      labels: { confirm: 'Usuń regał', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteRack.mutateAsync(place.rack),
    })
  }

  function openAddRackModal() {
    modals.openContextModal({
      modal: 'addRack',
      centered: true,
      title: 'Dodaj regał',
      innerProps: {},
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<PlaceConfig>>>(
    () => [
      {
        accessorKey: 'rack',
        header: 'Numer regału',
      },
      {
        accessorKey: 'numberOfColumns',
        header: 'Liczba kolumn',
      },
      {
        accessorKey: 'numberOfShelfes',
        header: 'Liczba półek',
      },
    ],
    []
  )

  const { data, isLoading, isError, error, isFetching, refetch } = usePlacesConfigs()
  const memoizedData = useMemo(() => data ?? [], [JSON.stringify(data)])

  const table = useMantineReactTable({
    columns,
    data: memoizedData,
    layoutMode: 'grid',
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
    initialState: {
      showGlobalFilter: false,
      showColumnFilters: true,
    },
    enableGlobalFilter: false,
    enableGlobalFilterModes: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableBottomToolbar: true,
    autoResetPageIndex: false,
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        <Tooltip label="Dodaj regał">
          <ActionIcon size="lg" variant="subtle" onClick={() => openAddRackModal()} ml="auto" mr={4}>
            <IconPlus />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Odśwież dane">
          <ActionIcon size="lg" variant="subtle" onClick={() => refetch()}>
            <IconRefresh />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),
    mantineToolbarAlertBannerProps: isError
      ? {
          color: 'red',
          children: `Wystąpił błąd podczas pobierania danych. (${error})`,
        }
      : undefined,
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    mantinePaginationProps: {
      rowsPerPageOptions: ['10', '15'],
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
      },
    },
    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Edytuj">
          <ActionIcon variant="light" color="gray" radius="xl" onClick={() => openUpdatePlaceConfigModal(row.original)}>
            <IconEdit size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Usuń">
          <ActionIcon radius="xl" variant="light" color="red" onClick={() => openDeleteRackModal(row.original)}>
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>
      </Flex>
    ),
  })

  return <MantineReactTable table={table} />
}
